import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import useChangeEffect from '../../../../hooks/useChangeEffect.js';
import { debounce } from 'lodash-es';
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import TableComponent from '../../../Table/Table.jsx';
import { tags } from '../../leads/tags.js';
import CustomTextField from '../../../commonComponents/CustomTextField.js';
import { useGetAllLeadsTermins } from '../../../../api/termins.js';
import useLeadTerminsColumns from '../../../../hooks/AllUsers/useLeadTerminsColumns.js';
import { handleLimit, handleNextPage, handlePrevPage } from '../../../../utils/paginationFunction.js';
import LanguageSelect from '../../../selectTranslation/translateSelect.js';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const LeadAppointments = () => {
  const { t } = useTranslation();

  const [keyword, setKeyword] = useState('');
  const [filterTag, setFilterTag] = useState(['search']);
  const [filterTagData, setFilterTagData] = useState({});
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);

  const { data: allUsers, isLoading, isFetching } = useGetAllLeadsTermins({ page, limit, ...filterTagData });

  const termins = allUsers?.results;

  const { control } = useForm({
    mode: 'onSubmit',

    reValidateMode: 'onChange',
  });

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    setFilterTag(typeof value === 'string' ? value.split(',') : value);
  };

  const updateFilterTagData = () => {
    setFilterTagData({ [filterTag]: keyword });
  };

  useChangeEffect(() => {
    updateFilterTagData();
  }, [filterTag, keyword]);

  const debouncedHandleSearchChange = debounce((value) => {
    setKeyword(value);
    setPage(0);
  }, 300);

  const columns = useLeadTerminsColumns();

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }} minHeight="100vh">
        <Box display="flex" flexDirection="column" width="100%">
          <Box display="flex" justifyContent="space-between" alignItems="center" px={4}>
            <Typography variant="h6" fontWeight={700} px={5} py={2.45} sx={{ marginLeft: -4 }}>
              {t('Lead Appointments')}
            </Typography>
            <LanguageSelect />
          </Box>
          <Divider />

          <Box m={5}>
            {termins && (
              <TableComponent
                hasAboveStuff
                data={termins}
                columns={columns}
                hasPagination
                rowsPerPage={allUsers?.limit}
                page={allUsers?.currentPage - 1}
                handleNextPage={() => handleNextPage(setPage)}
                handlePrevPage={() => handlePrevPage(setPage)}
                hasNextPage={allUsers?.hasNextPage}
                hasPreviousPage={allUsers?.hasPreviousPage}
                onPageChange={(lim) => handleLimit(lim, setLimit)}
                setPage={setPage}
                isLoading={isLoading}
                isFetching={isFetching}
                total={allUsers?.total}
              >
                <Box display="flex" alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    {t('Appointments')}
                  </Typography>
                </Box>
                <Box display="flex" gap="20px" alignItems="center">
                  <FormControl sx={{ width: 200 }}>
                    <InputLabel id="demo-multiple-checkbox-label">Tag</InputLabel>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      value={filterTag}
                      onChange={handleChange}
                      input={<OutlinedInput label="Tag" sx={{ border: '1px solid #DCE4E8', borderRadius: '15px' }} />}
                      renderValue={(selected) => {
                        const selectedTag = tags.find((tag) => tag.value === selected[0]);
                        return selectedTag ? selectedTag.label : '';
                      }}
                      MenuProps={MenuProps}
                    >
                      {tags?.map((tag) => (
                        <MenuItem key={tag.value} value={tag.value}>
                          <ListItemText primary={tag.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Controller
                    name="search"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Box display="flex" flexDirection="column">
                        <CustomTextField
                          id="search"
                          value={value ? value : ''}
                          label="Search"
                          onChange={(event) => {
                            onChange(event);
                            debouncedHandleSearchChange(event.target.value);
                          }}
                        />
                      </Box>
                    )}
                  />
                </Box>
              </TableComponent>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default LeadAppointments;
